import { createContext, useContext } from "react"

import { VertesiaClient } from "@vertesia/client"
import { InteractionRef } from "@vertesia/common"

import { SharedState, useWatchSharedState } from "@hooks/use-shared-state"

interface AvailableAgentModel {
    agents: InteractionRef[]
    agent: InteractionRef | undefined
    defaultAgent: InteractionRef | undefined
    chat: InteractionRef | undefined
    error?: Error
    loading: boolean
}

export class AvailableAgent {
    result = new SharedState<AvailableAgentModel>({
        agents: [],
        agent: undefined,
        defaultAgent: undefined,
        chat: undefined,
        loading: false,
    })

    constructor(public client: VertesiaClient) {}

    setAgent(agent: InteractionRef | undefined) {
        this.result.value = {
            ...this.result.value,
            agent: agent,
        }
    }

    get agents() {
        return this.result.value.agents
    }

    get error() {
        return this.result.value.error
    }

    get isLoading() {
        return this.result.value.loading
    }

    reset() {
        this.result.value = {
            agents: [],
            agent: undefined,
            defaultAgent: undefined,
            chat: undefined,
            loading: false,
        }
    }

    async _search() {
        if (this.isLoading) {
            return Promise.resolve(false)
        }

        this.result.value = {
            loading: true,
            agents: [],
            agent: undefined,
            defaultAgent: undefined,
            chat: undefined,
        }

        try {
            let agents: InteractionRef[] = []
            let agent: InteractionRef | undefined = undefined
            let defaultAgent: InteractionRef | undefined = undefined

            const agentInteractions = await this.client.interactions.list({ limit: 50, query: { tags: ["agent"] } })
            if (agentInteractions.length === 0) {
                agent = undefined
                defaultAgent = undefined
            } else {
                const tmp = agentInteractions.find((interaction) => interaction.tags.includes("default"))
                if (tmp) {
                    agent = tmp
                    defaultAgent = tmp
                } else {
                    agent = agentInteractions[0]
                    defaultAgent = agentInteractions[0]
                }
                agents = agentInteractions.filter((interaction) => interaction.id !== defaultAgent?.id)
            }

            let chat: InteractionRef | undefined = undefined
            const chatInteractions = await this.client.interactions.list({ limit: 50, query: { tags: ["chat"] } })
            if (chatInteractions.length) {
                const defaultChat = chatInteractions.find((interaction) => interaction.tags.includes("default"))
                chat = defaultChat || chatInteractions[0]
            }

            this.result.value = {
                loading: false,
                agents,
                agent,
                defaultAgent,
                chat,
            }

            return true
        } catch (err) {
            this.result.value = {
                error: err instanceof Error ? err : new Error("Unknown error occurred"),
                loading: false,
                agents: [],
                agent: undefined,
                defaultAgent: undefined,
                chat: undefined,
            }
            throw err
        }
    }

    search() {
        if (this.isLoading) {
            return Promise.resolve(false)
        }
        return this._search()
    }
}

export const AvailableAgentContext = createContext<AvailableAgent | undefined>(undefined)

export function useSearch() {
    return useContext(AvailableAgentContext)!
}

export function useWatchSearchResult() {
    const search = useSearch()
    const result = useWatchSharedState(search.result)
    return { ...result, search }
}
